'use client';
import { jsxs, jsx } from "react/jsx-runtime";
import { ToggleGroup, ToggleGroupItem } from "@radix-ui/react-toggle-group";
import { useScreenSize, useBirthDate, useDisclosure, useOnClickOutside, formatNumberWithZeroPrefix, cn, trimZeroPrefix, useScrollableRef } from "@tameeni/shared-logic";
import { endOfMonth, eachDayOfInterval } from "date-fns";
import React__default, { useState, useRef, useMemo, useCallback, useLayoutEffect, useEffect } from "react";
import { Drawer, DrawerTrigger, DrawerPortal, DrawerContent } from "../drawer/drawer.js";
import { Input } from "../input/input.js";
import { Popover, PopoverTrigger, PopoverContent, PopoverArrow } from "../popover/popover.js";
import { ScrollArea } from "../scroll-area/scroll-area.js";
import { Icon } from "@tameeni/icons";
import { useMask } from "@react-input/mask";
const disabledItemStyles = "opacity-30 cursor-not-allowed";
function DateSelectListComponent(props) {
  var _a;
  const {
    className,
    placeholderText,
    minAge,
    maxAge,
    locale = "en",
    variant = "g",
    hasDays = false,
    disabled,
    value,
    onChange,
    onAfterChange,
    disableDays,
    disableMonths,
    disabledYears,
    ...reset
  } = props;
  const [showYearLimitError, setShowYearLimitError] = useState(false);
  const [showMonthLimitError, setShowMonthLimitError] = useState(false);
  const [showDayLimitError, setShowDayLimitError] = useState(false);
  const [popoverWidth, setPopoverWidth] = useState(0);
  const triggerRef = useRef(null);
  const defaultMaskOptions = {
    mask: hasDays ? "yyyy/mm/dd" : "yyyy/mm",
    replacement: { d: /\d/, m: /\d/, y: /\d/ },
    showMask: true,
    separate: true
  };
  const internalInputRef = useMask(props.maskOptions || defaultMaskOptions);
  const inputRef = props.inputRef || internalInputRef;
  const { isMobile } = useScreenSize();
  const { getMonths, getYears } = useBirthDate();
  const clickOutsideRef = useRef(null);
  const {
    isOpened: isDatepickerOpened,
    onOpen: onDatePickerOpen,
    onClose: onDatePickerClose
  } = useDisclosure();
  const {
    isOpened: isInputFocused,
    onOpen: onInputFocus,
    onClose: onInputBlur
  } = useDisclosure();
  useOnClickOutside(clickOutsideRef, onDatePickerClose);
  const splittedValue = ((_a = value.replace(/(m|d|y)/g, "")) == null ? void 0 : _a.split("/")) ?? [];
  const currentDay = hasDays ? splittedValue[2] : "";
  const month = splittedValue[1];
  const year = splittedValue[0];
  const isDaySelectable = month && year && +month > 0 && +year > 0;
  const generatedYears = useMemo(
    () => getYears(variant, minAge, maxAge),
    [getYears, maxAge, minAge, variant]
  );
  const generatedMonths = useMemo(
    () => getMonths(variant, locale),
    [getMonths, locale, variant]
  );
  const generatedDays = useMemo(
    () => getDaysOfMonth({
      month: month ? +month : (/* @__PURE__ */ new Date()).getMonth(),
      year: year ? +year : (/* @__PURE__ */ new Date()).getFullYear()
    }).map((d) => ({ label: String(d), value: d })),
    [month, year]
  );
  const [minDay, maxDay] = useMemo(() => {
    const dayNumbers = generatedDays.map((m) => +m.value);
    return [Math.min(...dayNumbers), Math.max(...dayNumbers)];
  }, [generatedDays]);
  const [minMonth, maxMonth] = useMemo(() => {
    const monthNumbers = generatedMonths.map((m) => +m.value);
    return [Math.min(...monthNumbers), Math.max(...monthNumbers)];
  }, [generatedMonths]);
  const [minYear, maxYear] = useMemo(() => {
    const yearNumbers = generatedYears.map((y) => +y.value);
    return [Math.min(...yearNumbers), Math.max(...yearNumbers)];
  }, [generatedYears]);
  const clearLimitErrors = useCallback(() => {
    setShowYearLimitError(false);
    setShowMonthLimitError(false);
    setShowDayLimitError(false);
  }, []);
  const setCalendarValue = useCallback(
    (newDay = "", newMonth = "", newYear = "") => {
      clearLimitErrors();
      const d = newDay === "00" ? "" : newDay;
      const m = newMonth === "00" ? "" : newMonth;
      const newValue = hasDays ? `${newYear}/${m}/${d}` : `${newYear}/${m}`;
      onChange(newValue);
      onAfterChange == null ? void 0 : onAfterChange();
    },
    [clearLimitErrors, hasDays, onChange, onAfterChange]
  );
  const handleInputChange = useCallback(
    (e) => {
      const inputValue = e.target.value;
      const [y, m, d] = inputValue.split("/");
      console.log({ y, m, d });
      if ((disableDays == null ? void 0 : disableDays.includes(+d)) || (disableMonths == null ? void 0 : disableMonths.includes(+m)) || (disabledYears == null ? void 0 : disabledYears.includes(+y)) || d && (!y || !m))
        return;
      if (hasDays && d.length >= 2 && (+d < minDay || +d > maxDay) && !(disableDays == null ? void 0 : disableDays.includes(+d)))
        return setShowDayLimitError(true);
      if (m.length >= 2 && (+m < minMonth || +m > maxMonth) && !(disableMonths == null ? void 0 : disableMonths.includes(+m)))
        return setShowMonthLimitError(true);
      if (y.length >= 4 && (+y < minYear || +y > maxYear) && !(disabledYears == null ? void 0 : disabledYears.includes(+y)))
        return setShowYearLimitError(true);
      clearLimitErrors();
      onChange(inputValue);
    },
    [
      clearLimitErrors,
      disableDays,
      disableMonths,
      disabledYears,
      maxDay,
      maxMonth,
      maxYear,
      minDay,
      minMonth,
      minYear,
      onChange
    ]
  );
  const setSelectedDay = useCallback(
    (value2 = "") => {
      setCalendarValue(formatNumberWithZeroPrefix(Number(value2)), month, year);
    },
    [month, setCalendarValue, year]
  );
  const setSelectedMonth = useCallback(
    (value2 = "") => {
      setCalendarValue(
        currentDay,
        formatNumberWithZeroPrefix(Number(value2)),
        year
      );
    },
    [currentDay, setCalendarValue, year]
  );
  const setSelectedYear = useCallback(
    (value2 = "") => {
      setCalendarValue(currentDay, month, value2);
    },
    [currentDay, month, setCalendarValue]
  );
  useLayoutEffect(() => {
    function updatePopoverWidth() {
      if (typeof inputRef === "object" && inputRef !== null && "current" in inputRef && inputRef.current) {
        setPopoverWidth(inputRef.current.offsetWidth + 32);
      }
    }
    updatePopoverWidth();
    if (!isMobile) {
      window.addEventListener("resize", updatePopoverWidth);
    }
    return () => {
      window.removeEventListener("resize", updatePopoverWidth);
    };
  }, [inputRef, isMobile]);
  useScrollOnChange(year, 4, "year-value");
  useScrollOnChange(month, 2, "month-value");
  useScrollOnChange(currentDay, 2, "day-value");
  const gridColsStyles = hasDays ? "grid-cols-3" : "grid-cols-2";
  const DateSelectContent = /* @__PURE__ */ jsxs("div", { className: cn("m-h-full p-0", className), children: [
    /* @__PURE__ */ jsxs(
      "section",
      {
        className: cn(
          "grid rounded-t-lg bg-slate-100 border-b text-t3 sm:text-t2",
          gridColsStyles
        ),
        children: [
          /* @__PURE__ */ jsx(
            DateSelectHeaderCell,
            {
              title: locale === "ar" ? "السنة" : "Year",
              errorMessage: locale === "ar" ? `تتراوح السنة ما بين ${minYear} ~ ${maxYear}` : `year range is between ${minYear} ~ ${maxYear}`,
              hasErrors: showYearLimitError,
              isActive: Boolean(year)
            }
          ),
          /* @__PURE__ */ jsx(
            DateSelectHeaderCell,
            {
              title: hasDays ? locale === "ar" ? "الشهر" : "Month" : locale === "ar" ? "اختر الشهر" : "Select Month",
              errorMessage: locale === "ar" ? `يتراوح الشهر ما بين ${minMonth} ~ ${maxMonth}` : `month range is between ${minMonth} ~ ${maxMonth}`,
              hasErrors: showMonthLimitError,
              isActive: Boolean(month)
            }
          ),
          hasDays && /* @__PURE__ */ jsx(
            DateSelectHeaderCell,
            {
              title: locale === "ar" ? "اليوم" : "Day",
              errorMessage: locale === "ar" ? `يتراوح اليوم ما بين ${minDay} ~ ${maxDay}` : `day range is between ${minDay} ~ ${maxDay}`,
              hasErrors: showDayLimitError,
              isActive: Boolean(currentDay)
            }
          )
        ]
      }
    ),
    /* @__PURE__ */ jsxs("div", { className: cn("max-h-[20.75rem] grid ", gridColsStyles), children: [
      /* @__PURE__ */ jsx(
        DateScrollableColumn,
        {
          locale,
          currentSelectedValue: year,
          setSelectedValue: setSelectedYear,
          dateList: generatedYears,
          disabledValues: disabledYears,
          dataType: "year"
        }
      ),
      /* @__PURE__ */ jsx(
        DateScrollableColumn,
        {
          locale,
          currentSelectedValue: month,
          setSelectedValue: setSelectedMonth,
          dateList: generatedMonths,
          disabledValues: disableMonths,
          dataType: "month"
        }
      ),
      hasDays && /* @__PURE__ */ jsx(
        DateScrollableColumn,
        {
          locale,
          currentSelectedValue: currentDay,
          setSelectedValue: setSelectedDay,
          dateList: generatedDays,
          disabledValues: disableDays,
          dataType: "day",
          disabled: !isDaySelectable
        }
      )
    ] })
  ] });
  const DateInput = /* @__PURE__ */ jsx(
    Input,
    {
      ref: inputRef,
      suffix: /* @__PURE__ */ jsx(Icon, { className: "absolute end-4", name: "Calendar" }),
      type: "text",
      placeholder: placeholderText,
      disabled,
      className: "h-10",
      value,
      onChange: handleInputChange,
      readOnly: isMobile,
      ...reset
    }
  );
  return /* @__PURE__ */ jsx("div", { className: "z-30", children: isMobile ? /* @__PURE__ */ jsxs(Drawer, { children: [
    /* @__PURE__ */ jsx(DrawerTrigger, { asChild: true, children: DateInput }),
    /* @__PURE__ */ jsx(DrawerPortal, { children: /* @__PURE__ */ jsx(DrawerContent, { className: "h-[23rem] border rounded rounded-t-lg shadow", children: DateSelectContent }) })
  ] }) : /* @__PURE__ */ jsxs(Popover, { open: isDatepickerOpened, children: [
    /* @__PURE__ */ jsx(
      PopoverTrigger,
      {
        onClick: onDatePickerOpen,
        onFocus: () => {
          onInputFocus();
          onDatePickerOpen();
        },
        onBlur: onInputBlur,
        ref: triggerRef,
        asChild: true,
        children: DateInput
      }
    ),
    /* @__PURE__ */ jsx(
      PopoverContent,
      {
        ref: isInputFocused ? null : clickOutsideRef,
        side: "bottom",
        style: { width: popoverWidth },
        className: "md:max-w-full max-w-full w-full p-0 border rounded rounded-t-lg shadow",
        sideOffset: 12,
        children: DateSelectContent
      }
    )
  ] }) });
}
const DateSelectList = React__default.memo(DateSelectListComponent);
const useScrollOnChange = (value, exactLength, dataAttributeString) => {
  useEffect(() => {
    if ((value == null ? void 0 : value.length) === exactLength) {
      const element = document.querySelector(
        `[data-${dataAttributeString}="${trimZeroPrefix(value)}"]`
      );
      element == null ? void 0 : element.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "center"
      });
    }
  }, [dataAttributeString, exactLength, value]);
};
function getDaysOfMonth({ month, year }) {
  const startDate = new Date(year, month - 1, 1);
  const endDate = new Date(
    year,
    month - 1,
    endOfMonth(startDate).getDate()
  );
  const daysOfMonth = eachDayOfInterval({
    start: startDate,
    end: endDate
  });
  const dayNumbers = daysOfMonth.map((date) => date.getDate());
  return dayNumbers;
}
const DateSelectHeaderCell = (props) => {
  return /* @__PURE__ */ jsx(
    "h5",
    {
      className: cn(
        "text-start py-1.5 border-e ps-2 sm:ps-5 last-of-type:border-e-0",
        props.isActive && "text-blue",
        props.isDisabled && disabledItemStyles
      ),
      children: /* @__PURE__ */ jsxs(Popover, { open: props.hasErrors, children: [
        /* @__PURE__ */ jsx(PopoverTrigger, { asChild: true, children: /* @__PURE__ */ jsx("span", { children: props.title }) }),
        /* @__PURE__ */ jsxs(PopoverContent, { className: "focus-visible:outline-none", children: [
          props.errorMessage,
          /* @__PURE__ */ jsx(PopoverArrow, {})
        ] })
      ] })
    }
  );
};
const DateScrollableColumn = (props) => {
  const {
    locale,
    currentSelectedValue,
    setSelectedValue,
    dateList,
    disabledValues,
    dataType,
    disabled
  } = props;
  const scrollableRef = useScrollableRef(null);
  return /* @__PURE__ */ jsx(
    ScrollArea,
    {
      locale,
      className: "h-[20.75rem] border-e pt-2",
      dir: locale === "en" ? "ltr" : "rtl",
      disabled,
      children: /* @__PURE__ */ jsx(
        ToggleGroup,
        {
          type: "single",
          value: String(currentSelectedValue),
          className: "flex flex-col px-2",
          style: {
            direction: `${locale === "en" ? "ltr" : "rtl"}`
          },
          onValueChange: setSelectedValue,
          disabled,
          children: dateList == null ? void 0 : dateList.map(({ label, value }) => {
            const stringifiedValue = value;
            const isSelected = stringifiedValue === +currentSelectedValue;
            const isToggleDisabled = disabledValues == null ? void 0 : disabledValues.includes(+value);
            const dataAttributeKey = `data-${dataType}-value`;
            const dataAttributes = isToggleDisabled ? {} : { [dataAttributeKey]: stringifiedValue };
            return /* @__PURE__ */ jsx(
              ToggleGroupItem,
              {
                ref: isSelected ? scrollableRef : null,
                disabled: isToggleDisabled,
                value: String(stringifiedValue),
                className: cn(
                  "mb-1 block text-start ps-2 sm:px-4 rounded py-1 transition-colors duration-500 text-c1 sm:text-s1 font-semibold sm:font-normal",
                  isSelected ? "bg-blue-200 font-bold sm:font-semibold shadow-inner" : "hover:bg-slate-50",
                  (disabled || isToggleDisabled) && disabledItemStyles
                ),
                ...dataAttributes,
                children: label
              },
              value
            );
          })
        }
      )
    }
  );
};
export {
  DateSelectList
};
